/*
 * Feature flags area (audit Phase 3.3). Replaces wwwroot/js/Feature/List.js and
 * wwwroot/js/Feature/Resolve.js on the Phase 3.1 core. No jQuery.
 *
 * The Bindings tab and the Rollout tab are big enough to own their files --
 * areas/feature-bindings.ts and areas/feature-rollout.ts -- but all three are
 * one screen: the Feature Detail modal, whose tabs they fill. Anything shared
 * between them (the modal's id, which domain the tabs are scoped to, how a tab
 * reloads, how the list behind the modal learns it is stale) lives here and
 * they import it.
 *
 * Two pages render this area, and the difference between them is one attribute:
 * the global Features page swaps the whole view container when the list needs
 * redrawing, while the Features tab of an Application swaps only itself.
 * `#FeatureModal[data-application-id]` is what says which -- see hostApplicationId.
 *
 * Lazy tabs are core/tabs' job now; the legacy file bound a document-level
 * jQuery `shown.bs.tab` handler of its own. The strip does NOT carry
 * data-mcp-autotab: its active tab (General) is rendered inline rather than
 * fetched, and autoOpenTabs would open the first LAZY tab instead, which is not
 * the tab the dialog opened on.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { loadTab, markLoaded } from '../core/tabs';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

export const MODAL = 'FeatureModal';
export const TABS = 'FeatureDetailTabs';
export const BINDINGS_PANE = '#FeatureBindingsTab';
export const ROLLOUT_PANE = '#FeatureRolloutTab';
const CREATE_FORM = 'FeatureCreateForm';
const EDIT_FORM = 'FeatureEditForm';
const CREATE_ALERT = 'FeatureCreateMessageContainer';
export const EDIT_ALERT = 'FeatureEditMessageContainer';

/** The domain the console is pointed at. The Feature tabs scope their entity pickers to it. */
export function currentDomainId(): string {
    return (one('#CurrentDomainId') as HTMLInputElement | null)?.value ?? '0';
}

/**
 * The Application whose Features tab the modal was opened from, or 0 on the
 * global Features page. The placeholder carries it so that a write made inside
 * the modal knows which container to redraw.
 */
export function hostApplicationId(): number {
    const raw = (one(`#${MODAL}`) as HTMLElement | null)?.getAttribute('data-application-id') ?? '';
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * A Feature tab URL carrying the console's current domain.
 *
 * The three lazy tabs scope their entity pickers to a Domain, and for a GLOBAL
 * feature -- one with no owning application, and so no domain of its own --
 * the only answer available is the domain the operator is standing in. The
 * server takes it as a fallback (`model.PickerDomainId = Feature.DomainId ??
 * domainId ?? 0`), so a request made without it leaves a global feature's
 * pickers unscoped.
 */
export function withDomain(url: string): string {
    if (/[?&]domainId=/.test(url)) return url;
    return `${url}${url.includes('?') ? '&' : '?'}domainId=${encodeURIComponent(currentDomainId())}`;
}

/** The tab button that owns a pane, e.g. '#FeatureBindingsTab'. */
export function tabFor(paneSelector: string): Element | null {
    return one(`#${TABS} [data-bs-target="${paneSelector}"]`);
}

/** Re-fetch one tab of the open modal, leaving the operator's place in the others alone. */
export function reloadTab(paneSelector: string): Promise<void> {
    const tab = tabFor(paneSelector);
    if (!tab) return Promise.resolve();
    markLoaded(tab, false);
    return loadTab(tab);
}

/**
 * Make a tab fetch itself again the next time it is shown.
 *
 * Running a rollout stage writes bindings, so the Bindings tab the operator
 * looked at five seconds ago is now a lie; it is cheaper to mark it stale than
 * to fetch a tab nobody may open.
 */
export function markStale(paneSelector: string): void {
    const tab = tabFor(paneSelector);
    if (tab) markLoaded(tab, false);
}

/** The count badge on a tab header, kept in step with what the tab now holds. */
export function setTabBadge(paneSelector: string, count: number): void {
    const badge = tabFor(paneSelector)?.querySelector('.badge');
    if (badge) badge.textContent = String(count);
}

/*
 * The list behind the modal is rendered once, when the modal opens, so anything
 * a tab writes (a binding added, a stage run) leaves its Bindings count stale.
 * Reloading the list there is not an option -- it CONTAINS the modal, so the
 * swap would tear the open dialog out of the page -- hence a flag, and a redraw
 * when the dialog closes.
 */
let listDirty = false;
let boundHide = false;

export function markListDirty(): void {
    listDirty = true;
}

/** Install the one document-level listener this area needs. Called from main.ts. */
export function initFeatureArea(): void {
    if (boundHide) return;
    boundHide = true;
    document.addEventListener('hidden.bs.modal', event => {
        const node = event.target;
        if (!(node instanceof Element) || node.id !== MODAL) return;
        if (!listDirty) return;
        listDirty = false;
        void List.Reload();
    });
}

/**
 * Stamp the current domain onto the detail modal's lazy tab URLs.
 *
 * core/tabs fetches exactly what `data-tab-url` says, and the attribute is
 * written by the server, which does not know which domain the console is
 * pointed at -- that lives in the rail. The legacy code solved this by owning
 * the tab loader outright and appending the parameter there; this leaves the
 * loader alone and fixes up the three URLs as the dialog lands, which is also
 * what a reload of one tab then re-uses.
 */
export function enhanceFeatureTabs(root: ParentNode): void {
    const strips: Element[] = [];
    if (root instanceof Element && root.id === TABS) strips.push(root);
    strips.push(...Array.from(root.querySelectorAll(`#${TABS}`)));
    for (const strip of strips) {
        for (const tab of strip.querySelectorAll('[data-tab-url]')) {
            const url = tab.getAttribute('data-tab-url');
            if (url) tab.setAttribute('data-tab-url', withDomain(url));
        }
    }
}

export const List = {
    ShowDetail(featureId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `Feature/Detail/${featureId}`,
            title: null,
            errorTitle: `Feature ${featureId}`,
            errorMessage: 'Feature Detail failed to execute:',
        });
    },

    NewFeature(applicationId?: unknown): void {
        const appId = Number.parseInt(String(applicationId ?? ''), 10) || 0;
        void ajaxModal({
            modal: MODAL,
            url: appId > 0 ? `Feature/Create/${appId}` : 'Feature/Create',
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New Feature',
            errorMessage: 'Failed to render new feature form',
        });
    },

    NewFeatureSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const working = saving(CREATE_FORM);
        working(true);
        void ajaxJson({
            url: 'Feature',
            method: 'POST',
            data: serialize(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create feature',
            onSuccess: () => {
                toast('success', 'Feature created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create feature', summaryOf(data));
                return true;
            },
            onComplete: () => working(false),
        });
    },

    SaveDetail(featureId: number | string): void {
        const form = one(`#${EDIT_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const working = saving(EDIT_FORM);
        working(true);
        void ajaxJson({
            url: `Feature/${featureId}`,
            method: 'PUT',
            data: serialize(form),
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to save feature',
            onSuccess: () => {
                toast('success', 'Feature saved');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${EDIT_FORM}`);
                toast('danger', 'Failed to save feature', summaryOf(data));
                return true;
            },
            onComplete: () => working(false),
        });
    },

    /**
     * Finish a rollout (or a sunset): flip the default to the end state and drop
     * the bindings that merely agreed with it. The dialog has to say which of
     * the two is about to happen, because the surviving bindings differ.
     */
    Complete(featureId: number | string, defaultIsOn?: unknown): void {
        const sunset = defaultIsOn === true || defaultIsOn === 'true';
        void confirm({
            title: sunset ? 'Complete sunset' : 'Complete rollout',
            html: (sunset
                ? 'The default becomes <b>off</b> for everyone, and every binding that merely switched a target off is removed. '
                  + 'Bindings that keep a target <b>on</b> survive.'
                : 'The default becomes <b>on</b> for everyone, and every binding that merely switched a target on is removed. '
                  + 'Bindings that keep a target <b>off</b> survive.')
                + '<br/><br/>This is audited.',
            confirmText: 'Complete',
            variant: 'warning',
            onConfirm: () => {
                void ajaxJson({
                    url: `Feature/${featureId}/Complete`,
                    method: 'POST',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to complete',
                    onSuccess: () => {
                        toast('success', sunset ? 'Sunset completed' : 'Rollout completed');
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to complete'),
                });
            },
        });
    },

    DeleteFeature(featureId: number | string): void {
        void confirm({
            title: 'Delete Feature',
            html: 'Delete this feature and every binding on it?<br/><br/>'
                + '<span class="text-danger">Consuming applications asking about it will no longer find it. '
                + 'A feature with audit history cannot be deleted; override it off instead.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Feature/${featureId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to delete feature',
                    onSuccess: () => {
                        toast('success', 'Feature deleted');
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete feature'),
                });
            },
        });
    },

    /** The global page swaps the whole view; the Application tab swaps only itself. */
    Reload(): Promise<void> {
        const appId = hostApplicationId();
        return appId > 0
            ? ajaxSwap({
                container: 'ApplicationFeaturesTab',
                url: `Feature/Application/${appId}`,
                alertTitle: 'Failed to reload features',
            })
            : ajaxSwap({
                container: 'ViewContainer',
                url: 'Feature',
                alertTitle: 'Failed to reload features',
            });
    },
};

export const Resolve = {
    /**
     * Ask the exact question a consuming application would. Only the subjects
     * the operator actually picked travel: an empty picker means "not named",
     * which is a different question from "named as nothing".
     */
    Ask(featureId: number | string): void {
        const query = new URLSearchParams();
        const add = (name: string, id: string): void => {
            const value = Number.parseInt((document.getElementById(id) as HTMLInputElement | null)?.value ?? '', 10);
            if (Number.isFinite(value) && value > 0) query.append(name, String(value));
        };
        add('personId', `FeatureResolvePerson_${featureId}`);
        add('tenantId', `FeatureResolveTenant_${featureId}`);
        add('applicationId', `FeatureResolveApplication_${featureId}`);
        add('domainId', `FeatureResolveDomain_${featureId}`);
        const suffix = query.toString();
        void ajaxSwap({
            container: `FeatureResolveResult_${featureId}`,
            url: `Feature/${featureId}/Resolve${suffix ? `?${suffix}` : ''}`,
            alertTitle: 'Failed to resolve',
        });
    },
};

// ---------------------------------------------------------------- helpers

/** The save button's icon swap while a write is in flight. */
function saving(formId: string): (working: boolean) => void {
    return working => {
        for (const node of all(`#${formId} .fa-save`)) node.classList.toggle('d-none', working);
        for (const node of all(`#${formId} .fa-spinner`)) node.classList.toggle('d-none', !working);
    };
}
